import {
  DocumentCategoryDto,
  GetDocumentsByCategoryParams,
} from "apis/data-contracts";
import { DateIcon, ReloadIcon, SearchIcon } from "assets/icons";
import Board from "components/Common/Board/Board";
import SearchEmpty from "components/Common/Empty/SearchEmpty";
import { Table } from "components/Common/Table/Table";
import TableDatePickerMobile from "components/Common/Table/TableDatePickerMobile";
import TableInputMobile, {
  TableInputMobileButton,
} from "components/Common/Table/TableInputMobile";
import Pagination from "components/foundation/Pagination";
import Select from "components/foundation/Select/Select";
import Tab from "components/foundation/Tab";
import useMyDeskDocumentsQuery from "features/document/hooks/queries/useMyDeskDocumentsQuery";
import useMyDeskAutoCompleteQuery from "features/my-page/queries/useMyDeskAutoCompleteQuery";
import dayjs from "dayjs";
import { DATE_FORMAT } from "config/constant";
import {
  MyDeskSearchOptions,
  MyDeskSummaryStatus,
} from "models/document/MyDeskSearchOptions";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import styled, { css } from "styled-components";
import removeEmptyFields from "utils/removeEmptyFields";
import MyDeskStatusSummary from "../MyDeskStatusSummary";
import {
  analysisDocumentStatusList,
  documentStatusList,
  normalizeMyDeskDocumentStatus,
  MyDeskStatusFilterValue,
} from "../myDeskStatus";
import DeskTable from "../(web)/DeskTable";

interface FormData {
  search: string;
  startDate?: string;
  endDate?: string;
}

interface DeskTableBoardProps {
  categories: DocumentCategoryDto[];
  searchState: MyDeskSearchOptions;
  setSearchOptions: React.Dispatch<React.SetStateAction<MyDeskSearchOptions>>;
}

const DeskTableMobilePage = ({
  categories,
  searchState,
  setSearchOptions,
}: DeskTableBoardProps) => {
  const ANALYSIS_PENDING_SUMMARY_STATUS: MyDeskSummaryStatus =
    "ANALYSIS_PENDING";
  const methods = useForm<FormData>();

  const { selectedTab, page, ...searchOptions } = searchState;

  const { data } = useMyDeskDocumentsQuery(
    removeEmptyFields({
      categoryUuid: selectedTab.uuid,
      page: page - 1,
      size: 10,
      searchTerm: searchOptions.search,
      startDate:
        searchOptions.startDate &&
        dayjs(searchOptions.startDate).format("YYYY-MM-DD"),
      endDate:
        searchOptions.endDate &&
        dayjs(searchOptions.endDate).format("YYYY-MM-DD"),
      documentStatus: (searchOptions.summaryStatus ||
        searchOptions.documentStatus) as GetDocumentsByCategoryParams["documentStatus"],
    }),
  );
  const { data: autoCompleteData } = useMyDeskAutoCompleteQuery({
    searchTerm: methods.watch("search"),
  });

  const documents = data.data.result.contents;

  const handleSearch = (formData: FormData) => {
    setSearchOptions((prev) => ({ ...prev, ...formData }));
  };

  const [isReset, setIsReset] = useState<boolean>();

  const handleSummaryStatusChange = (value: MyDeskStatusFilterValue) => {
    if (selectedTab.name === "ANALYSIS" && value === "APPROVE_IN_PROGRESS") {
      setSearchOptions((prev) => ({
        ...prev,
        summaryStatus: ANALYSIS_PENDING_SUMMARY_STATUS,
        documentStatus: undefined,
        page: 1,
      }));
      return;
    }

    setSearchOptions((prev) => ({
      ...prev,
      summaryStatus: undefined,
      documentStatus: normalizeMyDeskDocumentStatus(value),
      page: 1,
    }));
  };

  const handleDocumentStatusChange = (value: MyDeskStatusFilterValue) => {
    setSearchOptions((prev) => ({
      ...prev,
      summaryStatus: undefined,
      documentStatus: normalizeMyDeskDocumentStatus(value),
      page: 1,
    }));
  };

  return (
    <DeskTableBoardMobileLayout>
      <div className="desk_tab-list">
        {categories.map((category) => {
          return (
            <DeskCategoryTab
              key={category.uuid}
              selected={category.uuid === selectedTab.uuid}
              value={category.uuid}
              onClick={() => {
                setSearchOptions({
                  search: "",
                  startDate: "",
                  endDate: "",
                  selectedTab: category,
                  summaryStatus: undefined,
                  page: 1,
                });
                methods.reset({
                  search: "",
                  startDate: "",
                  endDate: "",
                });
              }}
            >
              {category.name}({category.totalCount})
            </DeskCategoryTab>
          );
        })}
      </div>
      <MyDeskStatusSummary
        categoryUuid={selectedTab.uuid}
        selectedStatus={
          searchState.summaryStatus === ANALYSIS_PENDING_SUMMARY_STATUS
            ? "APPROVE_IN_PROGRESS"
            : searchState.documentStatus
        }
        onSelectStatus={handleSummaryStatusChange}
        className="desk_status-summary"
      />
      <Table.Header>
        <DeskTableHeader>
          <DeskTableRow1>
            <Table.TotalCount>
              총 {data.data.result.totalElements}건
            </Table.TotalCount>

            <Select
              placeholder="문서 상태"
              list={
                selectedTab.name === "ANALYSIS"
                  ? analysisDocumentStatusList
                  : documentStatusList
              }
              style={{ width: "10rem", height: "3.6rem" }}
              value={searchState.documentStatus || " "}
              onValueChange={(value) =>
                handleDocumentStatusChange(value as MyDeskStatusFilterValue)
              }
            />
          </DeskTableRow1>

          <DeskTableRow2>
            <form onSubmit={methods.handleSubmit(handleSearch)}>
              <FormProvider {...methods}>
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                  }}
                >
                  <TableInputMobile
                    autocomplete={autoCompleteData?.data.result.map((item) => ({
                      label: item,
                      value: item,
                    }))}
                  >
                    <SearchIcon />
                    검색
                  </TableInputMobile>
                  <TableDatePickerMobile
                    onDateRangeSelected={(dateRange) => {
                      setSearchOptions((prev) => ({
                        ...prev,
                        startDate: dateRange.start
                          ? dayjs(dateRange.start)
                              .format(DATE_FORMAT)
                              .toString()
                          : "",
                        endDate: dateRange.end
                          ? dayjs(dateRange.end).format(DATE_FORMAT).toString()
                          : "",
                      }));
                    }}
                    onClickReset={isReset}
                  >
                    <DateIcon />
                    등록일
                  </TableDatePickerMobile>
                  <TableInputMobileButton
                    variant="tertiary"
                    size="small"
                    onClick={() => {
                      setSearchOptions((prev) => ({
                        ...prev,
                        search: "",
                        startDate: "",
                        endDate: "",
                        page: 1,
                      }));
                      methods.reset({
                        search: "",
                        startDate: "",
                        endDate: "",
                      });

                      setIsReset(!isReset);
                    }}
                  >
                    <ReloadIcon />
                    초기화
                  </TableInputMobileButton>
                </div>
              </FormProvider>
            </form>
          </DeskTableRow2>
        </DeskTableHeader>
      </Table.Header>

      {data.data.result.totalElements === 0 && <SearchEmpty />}
      {data.data.result.totalElements > 0 && (
        <>
          <DeskTable
            categoryId={selectedTab.uuid}
            data={documents}
            totalData={data.data.result.totalElements}
            page={page}
          />
          <div
            style={{
              display: "flex",
            }}
          >
            <Pagination
              currentPage={page}
              lastPage={data.data.result.totalPages}
              onClickPageButton={(nextPage) =>
                setSearchOptions((prev) => ({ ...prev, page: nextPage }))
              }
              style={{
                margin: "2rem auto",
              }}
            />
          </div>
        </>
      )}
    </DeskTableBoardMobileLayout>
  );
};

export default DeskTableMobilePage;

const DeskTableBoardMobileLayout = styled(Board)`
  padding: 2rem 1.6rem;
  width: 100%;
  border-radius: 2rem 2rem 0rem 0rem;
  margin-top: 1.1rem;

  .desk_tab-list {
    display: flex;
    gap: 0;
    margin-bottom: 2.4rem;
  }

  .desk_status-summary {
    margin-bottom: 2rem;
  }
`;

const DeskCategoryTab = styled(Tab)`
  flex: 1 1 0;
  min-width: 0;
  height: 4.8rem;
  padding: 0 0.8rem;
  border-bottom: 0.2rem solid #edeef3;
  font-size: 1.5rem;
  line-height: 2.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  transition:
    background-color 0.2s ease,
    border-bottom-color 0.2s ease;

  ${({ selected }) =>
    selected &&
    css`
      background-color: #eee1e5;
      border-bottom-color: #a99198;
      border-radius: 0.5rem 0.5rem 0 0;
    `}
`;

const DeskTableHeader = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
`;

const DeskTableRow1 = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DeskTableRow2 = styled.div`
  display: flex;
  justify-content: flex-end;
`;
